import type { Genre } from '@/graphql/graphql';
import { ContentType } from '@/graphql/graphql';
import { GET_GENRES } from '@/graphql/queries/genres.graphql';
import { apolloClient } from './apollo-client';

export type FetchGenresListArgs = {
    contentType: ContentType;
};

type FetchGenresListResult = {
    data: Genre[];
    total: number;
};

const GENRES_CACHE_TTL_MS = 24 * 3600 * 1000;
const genresCache = new Map<
    string,
    {
        value: FetchGenresListResult;
        expiresAt: number;
    }
>();
const inFlightGenresRequests = new Map<string, Promise<FetchGenresListResult>>();

const getLocaleCacheKey = () => {
    if (typeof document === 'undefined') {
        return 'server';
    }

    return (document.documentElement.lang || 'unknown').toLowerCase();
};

const getGenresCacheKey = (contentType: ContentType) => `${contentType}:${getLocaleCacheKey()}`;

export async function fetchGenresList({ contentType }: FetchGenresListArgs) {
    const cacheKey = getGenresCacheKey(contentType);
    const now = Date.now();
    const cachedEntry = genresCache.get(cacheKey);
    if (cachedEntry && cachedEntry.expiresAt > now) {
        return cachedEntry.value;
    }

    const inFlightRequest = inFlightGenresRequests.get(cacheKey);
    if (inFlightRequest) {
        return inFlightRequest;
    }

    const requestPromise: Promise<FetchGenresListResult> = apolloClient
        .query({
            query: GET_GENRES,
            variables: { contentType },
        })
        .then((response) => {
            const result: FetchGenresListResult = {
                data: response.data.genres,
                total: response.data.genres.length,
            };

            genresCache.set(cacheKey, {
                value: result,
                expiresAt: Date.now() + GENRES_CACHE_TTL_MS,
            });

            return result;
        })
        .finally(() => {
            inFlightGenresRequests.delete(cacheKey);
        });

    inFlightGenresRequests.set(cacheKey, requestPromise);

    return requestPromise;
}

export async function fetchAllGenres({ contentType }: FetchGenresListArgs) {
    return await fetchGenresList({ contentType });
}
